import { useMutation, useQuery } from '@tanstack/react-query';

import {
  getBrand,
  getPlatform,
  getPlatformCredentials,
  getPlatformIntegrationStatuses,
  getPlatforms,
  updatePlatformIntegrationStatuses,
} from 'src/api/zing';
import { IntegrationAction } from 'src/constants';
import { getActionsLeft } from 'src/helpers/integration';
import { useUser, useUserPlatformId } from 'src/hooks/data/user';
import { getIntl } from 'src/services/intl/intl';
import { showMessage } from 'src/services/notify/notify';
import { queryClient } from 'src/services/react-query/query-client';
import { usePlatformsStore } from 'src/stores/platforms';
import type { IntegrationStatusValues, PlatformIntegrationStatusesTransformed } from 'src/types/platforms';
import { useShallow } from 'zustand/shallow';
import { usePlatformEnv } from './platform-integration/platform-env';

const usePlatformsState = usePlatformsStore.getState();

export const usePlatformsFilters = () =>
  usePlatformsStore(
    useShallow((state) => ({
      searchQuery: state.searchQuery,
    })),
  );

export const usePlatformsActions = () => ({
  setSearchQuery: usePlatformsState.setSearchQuery,
});

export const useGetPlatformsQuery = () => {
  const user = useUser();
  const { data, isPending } = useQuery({
    queryKey: ['platforms'],
    queryFn: async () => {
      try {
        if (user?.isAdmin || !user?.zingPlatformIdsCanAccess.length) {
          return getPlatforms();
        }

        const platform = await getPlatform(user.zingPlatformIdsCanAccess[0]);

        return [platform];
      } catch (_) {
        return [];
      }
    },
  });

  return { data, isPending };
};

export const useGetPlatformsData = () => {
  const { data: platforms, isPending } = useGetPlatformsQuery();
  const { searchQuery } = usePlatformsFilters();
  const searchQueryValue = searchQuery.trim().toLowerCase();
  const data = platforms
    ?.map((platformItem) => {
      const foundByPlatform =
        platformItem.platformName.toLowerCase().includes(searchQueryValue) ||
        platformItem.zingPlatformId.toLowerCase().includes(searchQueryValue);

      if (!foundByPlatform && searchQueryValue) {
        return {
          ...platformItem,
          brands: platformItem.brands.filter(
            ({ brandName, zingBrandId }) =>
              brandName.toLowerCase().includes(searchQueryValue) ||
              zingBrandId.toLowerCase().includes(searchQueryValue),
          ),
        };
      }

      return platformItem;
    })
    .filter(({ platformName, zingPlatformId, brands }) => {
      if (!searchQueryValue) {
        return true;
      }

      return (
        platformName.toLowerCase().includes(searchQueryValue) ||
        zingPlatformId.toLowerCase().includes(searchQueryValue) ||
        brands.some(
          ({ brandName, zingBrandId }) =>
            brandName.toLowerCase().includes(searchQueryValue) || zingBrandId.toLowerCase().includes(searchQueryValue),
        )
      );
    });

  return {
    data,
    isInProgress: isPending,
  };
};

export const useGetPlatformData = (platformId: string) => {
  const { data, isPending } = useQuery({
    queryKey: ['platforms', platformId],
    queryFn: () => getPlatform(platformId),
    enabled: !!platformId,
  });

  return {
    data,
    isInProgress: platformId ? isPending : false,
  };
};

export const useGetPlatformCredentialsData = (platformId: string, enabled: boolean) => {
  const isEnabled = !!platformId && enabled;
  const env = usePlatformEnv();
  const { data, isPending } = useQuery({
    queryKey: ['platforms', platformId, 'credentials', env],
    queryFn: () => getPlatformCredentials(platformId, env),
    enabled: isEnabled,
  });

  return {
    data,
    isInProgress: isEnabled ? isPending : false,
  };
};

export const useGetPlatformIntegrationStatusesData = (platformId: string) => {
  const { data, isPending } = useQuery({
    queryKey: ['platforms', platformId, 'integrationStatuses'],
    queryFn: () => getPlatformIntegrationStatuses(platformId),
    enabled: !!platformId,
  });

  return {
    data,
    isInProgress: platformId ? isPending : false,
  };
};

export const useUpdatePlatformIntegrationStatuses = (platformId: string, actionId: string) => {
  const queryKey = ['platforms', platformId, 'integrationStatuses'];

  const { mutateAsync } = useMutation({
    mutationFn: (variables: { stagingStatus: IntegrationStatusValues; prodStatus: IntegrationStatusValues }) =>
      updatePlatformIntegrationStatuses(platformId, actionId, variables),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey });

      const prev: PlatformIntegrationStatusesTransformed | undefined = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: PlatformIntegrationStatusesTransformed) =>
        old.map((oldItem) => {
          if (oldItem.actionId === actionId) {
            return {
              ...oldItem,
              ...variables,
            };
          }

          return oldItem;
        }),
      );

      return { prev };
    },
    onError: (_err, _variables, context) => {
      queryClient.setQueryData(queryKey, context?.prev);
      showMessage({
        type: 'error',
        content: getIntl().formatMessage({ id: 'error.undefined' }),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return mutateAsync;
};

export const useIsPlatformIntegrated = () => {
  const platformId = useUserPlatformId();
  const { data, isInProgress } = useGetPlatformIntegrationStatusesData(platformId);

  return {
    isIntegrated: data ? getActionsLeft(data, Object.values(IntegrationAction)) === 0 : false,
    isInProgress,
  };
};

export const useGetBrandData = (brandId: string) => {
  const { data, isPending } = useQuery({
    queryKey: ['brands', brandId],
    queryFn: () => getBrand(brandId),
  });

  return {
    data,
    isInProgress: isPending,
  };
};
